import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";

import {
  EmptyState,
  KernelLockup,
  Loading,
  SchemeToggle,
  resume,
  signIn,
  signOut,
} from "@lopie/kernel-ui";

import { onUnauthorizedCall, useTokenFrom } from "./lib/oncall-api.ts";
import Incidents from "./screens/Incidents.jsx";
import Rotations from "./screens/Rotations.jsx";
import Me from "./screens/Me.jsx";
import Setup from "./screens/Setup.jsx";

/**
 * The on-call pager.
 *
 * ## Three screens, and the order is the argument
 *
 * **What is on fire** first, because that is why anybody opens this at three
 * in the morning and every second spent finding it is a second of an outage.
 *
 * **Who is on call** second — the thing people check during the day.
 *
 * **You** third, and it exists mostly to answer one question: *why did it not
 * reach me?* The answer is almost always a number, and the screen says where
 * that number comes from rather than offering a box to type it into. That is
 * the port's whole argument rendered: the pager does not keep its own copy of
 * your phone number.
 *
 * **Set up** last, and last is the point. It is read once, by one person, on
 * the day the app is installed — every other screen is read during an
 * outage. Putting it first would put the rarest thing in front of the most
 * urgent one.
 *
 * It is here at all because it was not, and that was the gap: an app whose
 * rotations, ladders and services could only be created by posting JSON at it
 * is an app only its author can install (LO-106).
 */
export default function App() {
  const [session, setSession] = useState(null);

  // During render, not in an effect.
  //
  // Child effects run before the parent's, so a screen mounted by this
  // component calls the API *before* a parent effect could hand the client a
  // token — and the first request of every page load went out unauthenticated
  // and came back 401, with the screen showing "HTTP 401" over a signed-in
  // app bar. Found by opening it rather than by reading it.
  //
  // A ref rather than the value, because this registers a getter once and the
  // session arrives later: closing over `session` here would pin the client
  // to whatever it was on first render, which is null.
  const sessionRef = useRef(null);
  sessionRef.current = session;
  useTokenFrom(() => sessionRef.current?.token ?? null);

  // A refused token means sign in again. Registered here, beside the token
  // itself, because this component owns the session and the API client should
  // not have a view about the browser's location.
  onUnauthorizedCall(() => signIn());

  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [deliberate, setDeliberate] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("signedout")) {
      setDeliberate(true);
      setReady(true);
      return;
    }
    resume()
      .then((found) => {
        setSession(found);
        if (!found) signIn();
      })
      .catch((e) => setError(e.message))
      .finally(() => setReady(true));
  }, []);


  if (!ready) return <Loading label="Restoring your session" minHeight="100vh" />;

  if (!session) {
    return (
      <Box sx={{ p: 4, maxWidth: 560, mx: "auto" }}>
        <KernelLockup />
        <Alert severity={error ? "error" : "info"} variant="outlined" sx={{ mt: 3 }}>
          <AlertTitle>{error ? "Could not sign you in" : "Signed out"}</AlertTitle>
          {error ?? "Nothing here is readable without signing in."}
        </Alert>
        {deliberate ? (
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => signIn()}>
            Sign in
          </Button>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ gap: 2, flexWrap: "wrap" }}>
          <KernelLockup />
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6">On call</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {session.claims?.username ?? session.claims?.sub}
          </Typography>
          <SchemeToggle />
          <Button size="small" onClick={() => signOut()}>Sign out</Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 1100, mx: "auto" }}>
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          sx={{ mb: 2 }}
        >
          <Tab label="What is on fire" />
          <Tab label="Who is on call" />
          <Tab label="You" />
          <Tab label="Set up" />
        </Tabs>

        {tab === 0 ? <Incidents /> : null}
        {tab === 1 ? <Rotations /> : null}
        {tab === 2 ? <Me /> : null}
        {tab === 3 ? <Setup /> : null}
      </Box>
    </Box>
  );
}
